import { gql } from '@apollo/client';
import {
  BenefitFragment,
  CategoryFragment,
  ClientGoalsFragment,
  CompanyFragment,
  CurrencyFragment,
  OrderFragment,
  PaginationFragment,
  PaymentMethodFragment,
  PlanFragment,
  ProductFragment,
  PromoCodeFragment,
  SettingsFragment,
  ShopCartFragment,
  TaxFragment,
  UserFragment,
  VariantFragment,
  CriteriaFragment,
  BenefitRequestFragment,
  CatalogFragment,
  MultimediaFragment,
  ClientFragment,
  SubscriptionFragment,
  OrderPaymentFragment,
  GroupFragment,
  AddressFragment,
} from './fragments';

// USER -----------------------------------------------
export const CURRENT_USER = gql`
  query CURRENT_USER($id: String) {
    currentUser(id: $id) {
      user {
        ...UserFragment
      }
    }
  }
  ${UserFragment}
`;

export const SMALL_CURRENT_USER = gql`
  query SMALL_CURRENT_USER($id: String) {
    currentUser(id: $id) {
      user {
        _id
        firstLogin
        userType
        client {
          _id
          shopCart {
            _id
          }
        }
        photo {
          src
        }
        company {
          _id
          name
          addresses {
            _id
          }
          logo {
            src
          }
          companyDetails {
            goals {
              _id
              periods {
                _id
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_USER = gql`
  query GET_USER($filter: FilterFindOneUserInput!) {
    user(filter: $filter) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const GET_USER_IN_ACCOUNT_SUMMARY = gql`
  query GET_USER($filter: FilterFindOneUserInput!) {
    user(filter: $filter) {
      _id
      firstName
      lastName
      dni
      dniType
      userType
      email
      phone
      company {
        _id
        name
        dniType
        dni
      }
    }
  }
`;

export const GET_USER_IN_SHOP_CART_CONTEXT = gql`
  query GET_USER($filter: FilterFindOneUserInput!) {
    user(filter: $filter) {
      _id
      company {
        _id
        shopCart {
          ...ShopCartFragment
        }
      }
    }
  }
  ${ShopCartFragment}
`;

export const GET_USER_IN_UPDATE = gql`
  query GET_USER($filter: FilterFindOneUserInput!) {
    user(filter: $filter) {
      _id
      firstName
      lastName
      email
      phone
      active
      company {
        _id
        status
        dniType
        dni
        name
        email
        phone
        addresses {
          ...AddressFragment
        }
        logo {
          ...MultimediaFragment
        }
      }
    }
  }
  ${MultimediaFragment}
  ${AddressFragment}
`;

export const GET_USERS = gql`
  query GET_USERS(
    $filter: FilterFindManyUserInput
    $sort: SortFindManyUserInput
  ) {
    users(filter: $filter, sort: $sort) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const USER_PAGINATION = gql`
  query USER_PAGINATION(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyUserInput
    $sort: SortFindManyUserInput
  ) {
    userPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        _id
        firstName
        lastName
        userType
        email
        dni
        dniType
        phone
        active
        privilege
        firstLogin
        discountCoupons
        createdAt
        currentSubscription {
          ...SubscriptionFragment
        }
        photo {
          ...MultimediaFragment
        }
        company {
          ...CompanyFragment
        }
        client {
          ...ClientFragment
        }
        ...UserFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${UserFragment}
  ${PaginationFragment}
  ${MultimediaFragment}
  ${CompanyFragment}
  ${ClientFragment}
  ${SubscriptionFragment}
`;

export const GET_USERS_IN_SHOP_CART = gql`
  query GET_USERS_IN_SHOP_CART($data: GetUsersInShopCartInput) {
    getUsersInShopCart(data: $data) {
      _id
      firstName
      lastName
      discountCoupons
      client {
        user
        _id
      }
    }
  }
`;

// COMPANY  -----------------------------------------------

export const GET_COMPANY = gql`
  query GET_COMPANY(
    $filter: FilterFindOneCompanyInput
    $skip: Int
    $sort: SortFindOneCompanyInput
  ) {
    company(filter: $filter, skip: $skip, sort: $sort) {
      ...CompanyFragment
    }
  }
  ${CompanyFragment}
`;

export const SMALL_GET_COMPANY = gql`
  query SMALL_GET_COMPANY(
    $filter: FilterFindOneCompanyInput
    $skip: Int
    $sort: SortFindOneCompanyInput
  ) {
    company(filter: $filter, skip: $skip, sort: $sort) {
      _id
      name
      email
      dni
      dniType
      phone
      domain
      addresses {
        country
        state
        city
        municipality
        zip
        address1
        address2
        _id
      }
      companyDetails {
        structure
        saleChannels
        regions {
          _id
          name
          subRegions
          active
          createdAt
          updatedAt
        }
        goals {
          metric
          unit
          periods {
            target
            name
            criterias {
              lowerRange
              upperRange
              reward
              active
              _id
              createdAt
              updatedAt
            }
            unitTime
            current
            active
            startDate
            endDate
            _id
            createdAt
            updatedAt
          }
          active
          _id
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const COMPANY_EMPLOYEES = gql`
  query COMPANY_EMPLOYEES($data: CompanyEmployeesInput) {
    companyEmployees(data: $data) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

// CATEGORY  -----------------------------------------------

export const GET_CATEGORIES = gql`
  query Categories(
    $sort: SortFindManyCategoryInput
    $limit: Int
    $skip: Int
    $filter: FilterFindManyCategoryInput
  ) {
    categories(sort: $sort, limit: $limit, skip: $skip, filter: $filter) {
      ...CategoryFragment
    }
  }
  ${CategoryFragment}
`;

export const GET_CATEGORY = gql`
  query GET_CATEGORY(
    $filter: FilterFindOneCategoryInput
    $skip: Int
    $sort: SortFindOneCategoryInput
  ) {
    category(filter: $filter, skip: $skip, sort: $sort) {
      _id
      name
      active
    }
  }
`;

export const CATEGORY_PAGINATION = gql`
  query CATEGORY_PAGINATION(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyCategoryInput
    $sort: SortFindManyCategoryInput
  ) {
    categoryPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        _id
        name
        active
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
`;

// PRODUCT  -----------------------------------------------

export const GET_PRODUCT = gql`
  query GET_PRODUCT(
    $filter: FilterFindOneProductInput
    $skip: Int
    $sort: SortFindOneProductInput
  ) {
    product(filter: $filter, skip: $skip, sort: $sort) {
      ...ProductFragment
    }
  }
  ${ProductFragment}
`;

export const GET_PRODUCT_PROFILE = gql`
  query GET_PRODUCT_PROFILE($data: GetProductProfile) {
    getProductProfile(data: $data) {
      product {
        ...ProductFragment
      }
      related {
        ...ProductFragment
      }
      company {
        _id
        name
      }
    }
  }
  ${ProductFragment}
`;

export const PRODUCT_PAGINATION = gql`
  query PRODUCT_PAGINATION(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyProductInput
    $sort: SortFindManyProductInput
  ) {
    productPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        ...ProductFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
  ${ProductFragment}
`;

export const GET_CATALOGUE = gql`
  query GET_CATALOGUE($data: GetCatalogInput) {
    getCatalog(data: $data) {
      sections {
        count
        category {
          ...CategoryFragment
        }
        products {
          ...ProductFragment
        }
      }
    }
  }
  ${ProductFragment}
  ${CategoryFragment}
`;

export const GET_CATALOG = gql`
  query GET_CATALOG($data: GetCatalogInput) {
    getCatalog(data: $data) {
      ...CatalogFragment
    }
  }
  ${CatalogFragment}
`;

export const GET_CATEGORY_ALLIES = gql`
  query GET_CATEGORY_ALLIES($data: GetCategoryAlliesInput) {
    getCategoryAllies(data: $data) {
      category {
        ...CategoryFragment
      }
      allies {
        ...CompanyFragment
      }
    }
  }
  ${CategoryFragment}
  ${CompanyFragment}
`;

// VARIANT -----------------------------------------------

export const GET_VARIANT = gql`
  query GET_VARIANT(
    $filter: FilterFindOneVariantInput
    $skip: Int
    $sort: SortFindOneVariantInput
  ) {
    variant(filter: $filter, skip: $skip, sort: $sort) {
      ...VariantFragment
    }
  }
  ${VariantFragment}
`;

// CODES FROM VARIANT -----------------------------------------

export const GET_ELECTRONIC_CODES_FROM_VARIANT = gql`
  query ElectronicCodes($filter: FilterFindManyElectronicCodeInput) {
    electronicCodes(filter: $filter) {
      code
      status
      expirationDate
      _id
    }
  }
`;

// PAYMENT METHOD -----------------------------------------

export const PAYMENT_METHOD_PAGINATION = gql`
  query PAYMENT_METHOD_PAGINATION(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyPaymentMethodInput
    $sort: SortFindManyPaymentMethodInput
  ) {
    paymentMethodPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        ...PaymentMethodFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
  ${PaymentMethodFragment}
`;

export const GET_PAYMENT_METHOD = gql`
  query GET_PAYMENT_METHOD(
    $filter: FilterFindOnePaymentMethodInput
    $skip: Int
    $sort: SortFindOnePaymentMethodInput
  ) {
    paymentMethod(filter: $filter, skip: $skip, sort: $sort) {
      ...PaymentMethodFragment
    }
  }
  ${PaymentMethodFragment}
`;

export const GET_PAYMENT_METHODS = gql`
  query GET_PAYMENT_METHODS(
    $filter: FilterFindManyPaymentMethodInput
    $skip: Int
    $limit: Int = 100
    $sort: SortFindManyPaymentMethodInput
  ) {
    paymentMethods(filter: $filter, skip: $skip, limit: $limit, sort: $sort) {
      ...PaymentMethodFragment
    }
  }
  ${PaymentMethodFragment}
`;

// TAX -----------------------------------------

export const GET_TAX = gql`
  query GET_TAX(
    $skip: Int
    $filter: FilterFindOneTaxInput
    $sort: SortFindOneTaxInput
  ) {
    tax(skip: $skip, filter: $filter, sort: $sort) {
      ...TaxFragment
    }
  }
  ${TaxFragment}
`;

export const GET_TAXES = gql`
  query GET_TAXES(
    $skip: Int
    $filter: FilterFindManyTaxInput
    $sort: SortFindManyTaxInput
  ) {
    taxes(skip: $skip, filter: $filter, sort: $sort) {
      ...TaxFragment
    }
  }
  ${TaxFragment}
`;

export const TAX_PAGINATION = gql`
  query TAX_PAGINATION(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyTaxInput
    $sort: SortFindManyTaxInput
  ) {
    taxPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        ...TaxFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
  ${TaxFragment}
`;

// PROMOCODES -----------------------------------------

export const GET_PROMOCODE = gql`
  query GET_PROMOCODE(
    $filter: FilterFindOnePromoCodeInput
    $skip: Int
    $sort: SortFindOnePromoCodeInput
  ) {
    promoCode(filter: $filter, skip: $skip, sort: $sort) {
      ...PromoCodeFragment
    }
  }
  ${PromoCodeFragment}
`;

export const PROMOCODE_PAGINATION = gql`
  query PROMOCODE_PAGINATION(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyPromoCodeInput
    $sort: SortFindManyPromoCodeInput
  ) {
    promoCodePagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        ...PromoCodeFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
  ${PromoCodeFragment}
`;

export const VERIFY_PROMO_CODE = gql`
  query VERIFY_PROMO_CODE($data: VerifyPromoCodeInput) {
    verifyPromoCode(data: $data) {
      ...PromoCodeFragment
    }
  }
  ${PromoCodeFragment}
`;

// CURRENCIES -----------------------------------------

export const GET_CURRENCY = gql`
  query GET_CURRENCY(
    $filter: FilterFindOneCurrencyInput
    $skip: Int
    $sort: SortFindOneCurrencyInput
  ) {
    currency(filter: $filter, skip: $skip, sort: $sort) {
      ...CurrencyFragment
    }
  }
  ${CurrencyFragment}
`;

export const CURRENCY_PAGINATION = gql`
  query CURRENCY_PAGINATION(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyCurrencyInput
    $sort: SortFindManyCurrencyInput
  ) {
    currencyPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        ...CurrencyFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
  ${CurrencyFragment}
`;

export const GET_CURRENCIES = gql`
  query GET_CURRENCIES(
    $filter: FilterFindManyCurrencyInput
    $skip: Int
    $limit: Int = 100
    $sort: SortFindManyCurrencyInput
  ) {
    currencies(filter: $filter, skip: $skip, sort: $sort, limit: $limit) {
      ...CurrencyFragment
    }
  }
  ${CurrencyFragment}
`;

// PLANS -----------------------------------------

export const GET_PLAN = gql`
  query GET_PLAN(
    $filter: FilterFindOnePlanInput
    $skip: Int
    $sort: SortFindOnePlanInput
  ) {
    plan(filter: $filter, skip: $skip, sort: $sort) {
      ...PlanFragment
    }
  }
  ${PlanFragment}
`;

export const PLAN_PAGINATION = gql`
  query PLAN_PAGINATION(
    $page: Int
    $perPage: Int = 20
    $filter: FilterFindManyPlanInput
    $sort: SortFindManyPlanInput
  ) {
    planPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        ...PlanFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
  ${PlanFragment}
`;

export const GET_PLANS = gql`
  query GET_PLANS(
    $filter: FilterFindManyPlanInput
    $skip: Int
    $limit: Int = 100
    $sort: SortFindManyPlanInput
  ) {
    plans(filter: $filter, skip: $skip, sort: $sort, limit: $limit) {
      ...PlanFragment
    }
  }
  ${PlanFragment}
`;

// SETTINGS -----------------------------------------

export const GET_SETTINGS = gql`
  query GET_SETTINGS(
    $filter: FilterFindManySettingsInput
    $skip: Int
    $limit: Int = 100
    $sort: SortFindManySettingsInput
  ) {
    settings(filter: $filter, limit: $limit, skip: $skip, sort: $sort) {
      ...SettingsFragment
    }
  }
  ${SettingsFragment}
`;

// BENEFITS -----------------------------------------

export const GET_BENEFIT = gql`
  query GET_BENEFIT(
    $filter: FilterFindOneBenefitInput
    $skip: Int
    $sort: SortFindOneBenefitInput
  ) {
    benefit(filter: $filter, skip: $skip, sort: $sort) {
      ...BenefitFragment
    }
  }
  ${BenefitFragment}
`;

export const BENEFIT_PAGINATION = gql`
  query BENEFIT_PAGINATION(
    $page: Int
    $perPage: Int = 20
    $filter: FilterFindManyBenefitInput
    $sort: SortFindManyBenefitInput
  ) {
    benefitPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        ...BenefitFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${BenefitFragment}
  ${PaginationFragment}
`;

export const GET_BENEFITS = gql`
  query GET_BENEFITS(
    $filter: FilterFindManyBenefitInput
    $skip: Int
    $limit: Int = 100
    $sort: SortFindManyBenefitInput
  ) {
    benefits(filter: $filter, skip: $skip, limit: $limit, sort: $sort) {
      ...BenefitFragment
    }
  }
  ${BenefitFragment}
`;

// SHOPCART -----------------------------------------

export const GET_SHOP_CART = gql`
  query GET_SHOP_CART(
    $filter: FilterFindOneShopCartInput
    $skip: Int
    $sort: SortFindOneShopCartInput
  ) {
    shopCart(filter: $filter, skip: $skip, sort: $sort) {
      ...ShopCartFragment
    }
  }
  ${ShopCartFragment}
`;

export const GET_SHOP_CART_TALLY = gql`
  query GET_SHOP_CART_TALLY($data: GetShopCartTallyInput) {
    getShopCartTally(data: $data) {
      subTotal
      total
      taxBase
      discountTotal
      finalTotal
      taxTotal
    }
  }
`;

// ORDERS -----------------------------------------

export const ORDER_PAGINATION = gql`
  query ORDER_PAGINATION(
    $page: Int
    $perPage: Int = 20
    $filter: FilterFindManyOrderInput
    $sort: SortFindManyOrderInput
  ) {
    orderPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        _id
        code
        status
        total
        createdAt
        user {
          firstName
          lastName
          company {
            name
          }
        }
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
`;

export const GET_ORDER = gql`
  query GET_ORDER(
    $filter: FilterFindOneOrderInput
    $skip: Int
    $sort: SortFindOneOrderInput
  ) {
    order(filter: $filter, skip: $skip, sort: $sort) {
      ...OrderFragment
    }
  }
  ${OrderFragment}
`;

export const GET_DISPLAY_ORDER = gql`
  query GetDisplayOrder($data: GetDisplayOrderInput) {
    getDisplayOrder(data: $data) {
      variant
      photo {
        src
        alt
        position
        variant
        description
        active
        _id
        createdAt
        updatedAt
      }
      benefit
      plan
      price
      title
      shopList {
        client
        quantity
        benefitPrice
        availableDate
      }
    }
  }
`;

export const GET_USERS_FROM_ORDER = gql`
  query GET_USERS_FROM_ORDERS($data: GetUsersFromOrderInput) {
    getUsersFromOrder(data: $data) {
      ...UserFragment
    }
  }
  ${UserFragment}
`;

export const GET_USERS_CLIENT_FROM_ORDER = gql`
  query GET_USERS_FROM_ORDERS($data: GetUsersFromOrderInput) {
    getUsersFromOrder(data: $data) {
      client {
        ...ClientFragment
      }
    }
  }
  ${ClientFragment}
`;

export const GET_ORDER_PRODUCT_BY_ID = gql`
  query GET_ORDER_PRODUCT_BY_ID($data: UseOrderProductInput) {
    getOrderProductById(data: $data) {
      _id
      title
      client {
        user {
          email
          firstName
          lastName
          phone
          dni
        }
        birthDate
      }
      company {
        name
        email
        phone
        active
      }
      electronicCode {
        code
        status
      }
      product {
        title
        slug
      }
      used
      active
      isOnline
    }
  }
`;

export const GET_ORDER_PAGINATION = gql`
  query GetOrdersPagination($data: GetOrdersPaginationInput) {
    getOrdersPagination(data: $data) {
      count
      pageInfo {
        ...PaginationFragment
      }
      items {
        _id
        createdAt
        code
        status
        paymentStatus
        subTotal
        discountTotal
        taxBase
        taxTotal
        total
        active
        user {
          ...UserFragment
        }
      }
    }
  }
  ${UserFragment}
  ${PaginationFragment}
`;
// ----------------------------------------- SUBSCRIPTIONS -----------------------------------------

export const GET_EMPLOYEES_SUBSCRIPTIONS = gql`
  query GET_EMPLOYEES_SUBSCRIPTIONS($data: getEmployeesSubscriptionsInput) {
    getEmployeesSubscriptions(data: $data) {
      subscriptions {
        plan {
          ...PlanFragment
        }
        employees
      }
    }
  }
  ${PlanFragment}
`;

export const GET_EMPLOYEES_SUBSCRIPTIONS_PAGINATION = gql`
  query GET_EMPLOYEES_SUBSCRIPTIONS_PAGINATION(
    $data: getEmployeesSubscriptionsPaginationInput
  ) {
    getEmployeesSubscriptionsPagination(data: $data) {
      items {
        firstName
        lastName
        currentSubscription {
          _id
          active
          startDate
          expiryDate
          plan {
            ...PlanFragment
          }
          frequency {
            time
            price
            _id
          }
        }
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PlanFragment}
  ${PaginationFragment}
`;

// GOALS -----------------------------------------
export const GET_CLIENT_GOAL = gql`
  query GET_CLIENT_GOAL(
    $filter: FilterFindOneClientGoalsInput
    $skip: Int
    $sort: SortFindOneClientGoalsInput
  ) {
    clientGoal(filter: $filter, skip: $skip, sort: $sort) {
      ...ClientGoalsFragment
    }
  }
  ${ClientGoalsFragment}
`;

export const GET_USER_CLIENT_GOALS = gql`
  query GET_USER_CLIENT_GOALS($data: GetUserClientGoalsInput) {
    getUserClientGoals(data: $data) {
      ...ClientGoalsFragment
    }
  }
  ${ClientGoalsFragment}
`;

export const GET_USERS_CLIENTS_GOAL = gql`
  query GET_USERS_CLIENTS_GOAL($filter: FilterFindManyClientGoalsInput) {
    clientGoals(filter: $filter) {
      ...ClientGoalsFragment
      user {
        ...UserFragment
      }
      company {
        ...CompanyFragment
      }
    }
  }
  ${ClientGoalsFragment}
  ${UserFragment}
  ${CompanyFragment}
`;

export const GET_USERS_WITHOUT_GOALS = gql`
  query GET_USERS_WITHOUT_GOALS($data: EmployeeNotPeriodInput) {
    employeesNotPeriod(data: $data) {
      email
      firstName
      lastName
      phone
      _id
      company {
        ...CompanyFragment
      }
    }
  }
  ${CompanyFragment}
`;

// DASHBOARD -----------------------------------------

// leaderboard
export const GET_COMPANY_EMPLOYEES_REWARDS = gql`
  query GetCompanyEmployeesRewards($data: GetCompanyEmployeesRewards) {
    getCompanyEmployeesRewards(data: $data) {
      leaderBoard {
        rewarded
        user {
          ...UserFragment
        }
      }
    }
  }
  ${UserFragment}
`;

// pie
export const GET_COMPANY_EMPLOYEES_GOALS = gql`
  query GetCompanyEmployeesGoals($data: GetCompanyEmployeesGoals) {
    getCompanyEmployeesGoals(data: $data) {
      goalCriteria {
        amount
        criteria {
          ...CriteriaFragment
        }
      }
    }
  }
  ${CriteriaFragment}
`;

export const GET_COMPANY_METRICS = gql`
  query GetCompanyMetrics($data: GetCompanyMetrics) {
    getCompanyMetrics(data: $data) {
      metrics {
        metric
        key
      }
    }
  }
`;

export const GET_TIME_PERIOD_ALLY_SALES = gql`
  query GetTimePeriodAllySales($data: GetSalesDashboardInput) {
    getTimePeriodAllySales(data: $data) {
      topAllies {
        company {
          ...CompanyFragment
        }
        sales
      }
    }
  }
  ${CompanyFragment}
`;

export const GET_TIME_PERIOD_CATEGORY_SALES = gql`
  query GetTimePeriodCategorySales($data: GetSalesDashboardInput) {
    getTimePeriodCategorySales(data: $data) {
      topCategories {
        category {
          ...CategoryFragment
        }
        sales
      }
    }
  }
  ${CategoryFragment}
`;

export const GET_SALES_IN_TIME_SCALE = gql`
  query GetSalesInTimeScale($data: GetSalesInTimeScale) {
    getSalesInTimeScale(data: $data) {
      timeScales
    }
  }
`;

export const GET_TOP_SELLING_PRODUCTS = gql`
  query GetTopSellingProducts($data: GetSalesDashboardInput) {
    getTopSellingProducts(data: $data) {
      topProducts {
        amount
        product {
          ...ProductFragment
        }
      }
    }
  }
  ${ProductFragment}
`;

export const GET_ACTIVE_CLIENTS = gql`
  query GetActiveClients($data: GetSalesDashboardInput) {
    getActiveClients(data: $data) {
      amount
    }
  }
`;

export const GET_TIME_PERIOD_ORDERS = gql`
  query GetTimePeriodOrders($data: GetSalesDashboardInput) {
    getTimePeriodOrders(data: $data) {
      total
      difference
    }
  }
`;

export const GET_TIME_PERIOD_CANCELED_ORDERS = gql`
  query GetTimePeriodCanceledOrders($data: GetSalesDashboardInput) {
    getTimePeriodCanceledOrders(data: $data) {
      total
      difference
    }
  }
`;

export const GET_TIME_PERIOD_SALES = gql`
  query GetTimePeriodSales($data: GetSalesDashboardInput) {
    getTimePeriodSales(data: $data) {
      total
      difference
    }
  }
`;

export const GET_TIME_PERIOD_PAID_SALES = gql`
  query GetTimePeriodPaidSales($data: GetSalesDashboardInput) {
    getTimePeriodPaidSales(data: $data) {
      total
      difference
    }
  }
`;

export const GET_TIME_PERIOD_PRODUCT_SALES = gql`
  query GetTimePeriodProductSales($data: GetSalesDashboardInput) {
    getTimePeriodProductSales(data: $data) {
      total
      difference
    }
  }
`;

export const GET_TIME_PERIOD_AVERAGE_TICKET = gql`
  query GetTimePeriodAverageTicket($data: GetSalesDashboardInput) {
    getTimePeriodAverageTicket(data: $data) {
      difference
      total
    }
  }
`;

// ----------------------------------------- REQUESTS -----------------------------------------

export const GET_BENEFITS_REQUESTS = gql`
  query GetBenefitRequests(
    $filter: FilterFindManyBenefitRequestInput
    $skip: Int
    $limit: Int
    $sort: SortFindManyBenefitRequestInput
  ) {
    getBenefitRequests(
      filter: $filter
      skip: $skip
      limit: $limit
      sort: $sort
    ) {
      ...BenefitRequestFragment
    }
  }
  ${BenefitRequestFragment}
`;

export const GET_BENEFIT_REQUEST = gql`
  query GetBenefitRequest(
    $filter: FilterFindOneBenefitRequestInput
    $skip: Int
    $sort: SortFindOneBenefitRequestInput
  ) {
    getBenefitRequest(filter: $filter, skip: $skip, sort: $sort) {
      ...BenefitRequestFragment
    }
  }
  ${BenefitRequestFragment}
`;

export const GET_REQUEST_AMOUNT_BY_COMPANY = gql`
  query GetRequestAmountByCompany($data: GetRequestAmountByCompany) {
    getRequestAmountByCompany(data: $data) {
      resp {
        company {
          ...CompanyFragment
        }
        amount
      }
    }
  }
  ${CompanyFragment}
`;

export const GET_COMPANY_BENEFITS_REQUEST = gql`
  query GetCompanyBenefitRequests($data: GetCompanyBenefitRequestsInput) {
    getCompanyBenefitRequests(data: $data) {
      ...BenefitRequestFragment
    }
  }
  ${BenefitRequestFragment}
`;

// ----------------------------------------- DASHBOARDINFO -----------------------------------------
export const GET_COMPANY_METRICS_RESULT = gql`
  query GetCompanyMetricsResult($data: ClientGoalsMetricInput) {
    getCompanyMetricsResult(data: $data) {
      expectedResult
      result
      labels
      target
      totalTarget
      totalCollected
    }
  }
`;

export const GET_COMPANY_EMPLOYEE_METRIC = gql`
  query GetCompanyEmployeeMetricsResult($data: ClientGoalsMetricInput) {
    getCompanyEmployeeMetricsResult(data: $data) {
      totalRewarded
      user {
        ...UserFragment
      }
    }
  }
  ${UserFragment}
`;

export const GET_TOTAL_REWARDED_BY_COMPANY = gql`
  query GetTotalRewardedByCompany($data: GetTotalRewardedInput) {
    getTotalRewardedByCompany(data: $data) {
      totalRewarded
    }
  }
`;

export const GET_USERS_TOTAL_POINTS_BY_COMPANY = gql`
  query GetUsersTotalPointsByCompany($data: CompanyEmployeesInput) {
    getUsersTotalPointsByCompany(data: $data) {
      totalPoints
    }
  }
`;

// ----------------------------------------- PAYABLE ACCOUNTS -----------------------------------------
export const GET_PAYABLE_ACCOUNTS_PAGINATION_REQUESTS = gql`
  query AccountPagination(
    $page: Int
    $perPage: Int
    $filter: FilterFindManyAccountInput
    $sort: SortFindManyAccountInput
  ) {
    accountPagination(
      page: $page
      perPage: $perPage
      filter: $filter
      sort: $sort
    ) {
      count
      items {
        _id
        total
        cutoffDate
        expirationDate
        active
        ally {
          ...CompanyFragment
        }
        orderPayments {
          ...OrderPaymentFragment
        }
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
  ${CompanyFragment}
  ${OrderPaymentFragment}
`;

export const GET_PAYABLE_ACCOUNTS_REQUESTS = gql`
  query Accounts {
    accounts {
      _id
      active
      ally {
        ...CompanyFragment
      }
      cutoffDate
      expirationDate
      orderPayments {
        ...OrderPaymentFragment
      }
    }
  }
  ${CompanyFragment}
  ${OrderPaymentFragment}
`;

export const GET_PAYABLE_ACCOUNT_REQUEST = gql`
  query Account($filter: FilterFindOneAccountInput) {
    account(filter: $filter) {
      _id
      total
      cutoffDate
      expirationDate
      active
      ally {
        ...CompanyFragment
      }
      orderPayments {
        ...OrderPaymentFragment
      }
    }
  }
  ${CompanyFragment}
  ${OrderPaymentFragment}
`;

export const GET_COMPANY_POINTS = gql`
  query GetCompanyTotalPoints($data: GetCompanyDashboardInput) {
    getCompanyTotalPoints(data: $data) {
      totalPoints
    }
  }
`;

export const GET_ALL_CATEGORIES_WITH_ALLIES = gql`
  query Allies {
    getAllCategoriesWithAllies {
      allies {
        ...CompanyFragment
      }
      category {
        ...CategoryFragment
      }
    }
  }
  ${CompanyFragment}
  ${CategoryFragment}
`;

export const GET_CATALOG_FILTER_PAGINATION = gql`
  query GET_CATALOG_FILTER_PAGINATION($data: GetCatalogFilterPaginationInput) {
    getCatalogFilterPagination(data: $data) {
      count
      items {
        ...ProductFragment
      }
      pageInfo {
        currentPage
        perPage
        pageCount
        itemCount
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${ProductFragment}
`;
// ----------------------------------------- GROUPS -----------------------------------------
export const GET_GROUPS = gql`
  query Groups {
    groups {
      company
      name
      pointsToAssign
      src
      users
      active
      _id
    }
  }
`;

export const GET_GROUP_PAGINATION = gql`
  query Groups($filter: FilterFindManyGroupInput, $page: Int, $perPage: Int) {
    groupPagination(filter: $filter, page: $page, perPage: $perPage) {
      count
      items {
        ...GroupFragment
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${GroupFragment}
  ${PaginationFragment}
`;

export const GET_GROUP_BY_ID = gql`
  query GetGroupById($data: GroupIdInput) {
    getGroupById(data: $data) {
      company
      name
      pointsToAssign
      src
      active
      users {
        _id
        email
        firstName
        lastName
        phone
        dni
      }
    }
  }
`;

// ----------------------------------------- PAYMENT PROCESS -----------------------------------------
export const GET_PAGO_MOVIL_PAYMENTS = gql`
  query MobilePaymentPagination($data: PaymentPaginationInput) {
    mobilePaymentPagination(data: $data) {
      items {
        amount
        reference
        status
        statusCode
        success
        metadata
        message
        paymentMethod {
          title
          type
          _id
        }
        createdAt
        user {
          firstName
          lastName
        }
      }
      pageInfo {
        ...PaginationFragment
      }
    }
  }
  ${PaginationFragment}
`;
